import { useDraggable } from "@dnd-kit/core";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ELEMENT_CATEGORIES, ELEMENT_TYPES, SECTION_DEFS } from "@/lib/elements";
import * as LucideIcons from "lucide-react";

function DraggableItem({ type }) {
  const def = ELEMENT_TYPES[type];
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `toolbox-${type}`,
    data: { fromToolbox: true, elementType: type },
  });
  const IconComponent = LucideIcons[def.icon] || LucideIcons.Box;
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      data-testid={`toolbox-item-${type}`}
      className={`flex items-center gap-2.5 px-3 py-2 rounded-md border border-transparent hover:border-gray-200 hover:bg-gray-50 cursor-grab active:cursor-grabbing transition-all ${
        isDragging ? "opacity-50" : ""
      }`}
    >
      <IconComponent className="w-3.5 h-3.5 text-gray-400 shrink-0" />
      <div className="min-w-0">
        <p className="text-xs font-medium text-gray-700 truncate">{def.label}</p>
        <p className="text-[9px] text-gray-400 truncate">{def.description}</p>
      </div>
    </div>
  );
}

function DraggableCustomItem({ element }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `toolbox-custom-${element.id}`,
    data: { fromToolbox: true, elementType: "custom", customElementDef: element },
  });
  const IconComponent = LucideIcons[element.icon] || LucideIcons.Box;
  const secDef = SECTION_DEFS[element.defaultSection];

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      data-testid={`toolbox-custom-${element.id}`}
      className={`flex items-center gap-2.5 px-3 py-2 rounded-md border border-transparent hover:border-gray-200 hover:bg-gray-50 cursor-grab active:cursor-grabbing transition-all ${
        isDragging ? "opacity-50" : ""
      }`}
    >
      <IconComponent className="w-3.5 h-3.5 text-gray-400 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-gray-700 truncate">{element.name}</p>
        <div className="flex items-center gap-1">
          <div className="w-1 h-1 rounded-full" style={{ backgroundColor: secDef?.color || "#666" }} />
          <p className="text-[9px] text-gray-400 truncate">
            {element.description || `${(element.fields || []).length} fields`}
          </p>
        </div>
      </div>
    </div>
  );
}

function DraggableSnippetItem({ snippet, onDelete }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `toolbox-snippet-${snippet.id}`,
    data: { fromToolbox: true, elementType: "snippet", snippetDef: snippet },
  });
  const IconComponent = LucideIcons[snippet.icon] || LucideIcons.Bookmark;
  const elemCount = (snippet.elements || []).length;
  const secDef = SECTION_DEFS[snippet.sectionHint || "body"];

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      data-testid={`toolbox-snippet-${snippet.id}`}
      className={`group flex items-center gap-2.5 px-3 py-2 rounded-md border border-transparent hover:border-amber-200 hover:bg-amber-50/40 cursor-grab active:cursor-grabbing transition-all ${
        isDragging ? "opacity-50" : ""
      }`}
    >
      <IconComponent className="w-3.5 h-3.5 text-amber-500 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-gray-700 truncate">{snippet.name}</p>
        <div className="flex items-center gap-1">
          <div className="w-1 h-1 rounded-full" style={{ backgroundColor: secDef?.color || "#666" }} />
          <p className="text-[9px] text-gray-400 truncate">
            {elemCount === 1 ? "1 element" : `${elemCount} elements`}
          </p>
        </div>
      </div>
      {onDelete && (
        <button
          type="button"
          onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(snippet); }}
          title="Delete snippet"
          data-testid={`snippet-delete-${snippet.id}`}
          className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition px-1"
        >
          <LucideIcons.X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

export function ElementToolbox({ customElements = [], snippets = [], onDeleteSnippet }) {
  // Split snippets into starter pack (built-in, can't be deleted) vs My Snippets.
  const starterSnippets = snippets.filter((s) => s.isStarter);
  const userSnippets    = snippets.filter((s) => !s.isStarter);

  return (
    <div
      className="w-56 bg-white border-r border-gray-200 flex flex-col shrink-0"
      data-testid="element-toolbox"
    >
      <div className="px-4 py-3 border-b border-gray-100">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">
          Elements
        </p>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-4">
          {ELEMENT_CATEGORIES.map((cat) => (
            <div key={cat.name}>
              <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider px-3 mb-1">
                {cat.name}
              </p>
              {cat.types.map((type) => (
                <DraggableItem key={type} type={type} />
              ))}
            </div>
          ))}
          {customElements.length > 0 && (
            <div>
              <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider px-3 mb-1">
                Custom
              </p>
              {customElements.map((el) => (
                <DraggableCustomItem key={el.id} element={el} />
              ))}
            </div>
          )}
          {starterSnippets.length > 0 && (
            <div data-testid="starter-snippets-section">
              <p className="text-[10px] text-emerald-600 font-semibold uppercase tracking-wider px-3 mb-1 flex items-center gap-1.5">
                <LucideIcons.Sparkles className="w-2.5 h-2.5" />
                Starter Pack
              </p>
              {starterSnippets.map((sn) => (
                /* Starter snippets are read-only — no onDelete handler is passed
                   so the trash button never renders on hover. */
                <DraggableSnippetItem key={sn.id} snippet={sn} />
              ))}
            </div>
          )}
          {userSnippets.length > 0 && (
            <div data-testid="snippets-section">
              <p className="text-[10px] text-amber-500 font-semibold uppercase tracking-wider px-3 mb-1 flex items-center gap-1.5">
                <LucideIcons.Bookmark className="w-2.5 h-2.5" />
                My Snippets
              </p>
              {userSnippets.map((sn) => (
                <DraggableSnippetItem key={sn.id} snippet={sn} onDelete={onDeleteSnippet} />
              ))}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
